"use client";
import { motion } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

export default function TimelineSection() {
  const { isDark } = useTheme();

  // ✅ SEO: keyword-rich descriptions — targets trust, transparency, delivery, support
  const items = [
    {
      title: "Proven Technical Expertise",
      description: "Our experienced engineers deliver high-quality web, mobile, AI, and embedded solutions tailored to every client's unique business needs.",
    },
    {
      title: "Transparent Development Process",
      description: "We maintain open communication, regular progress updates, and complete project clarity — from discovery and design through to deployment.",
    },
    {
      title: "On-Time Project Delivery",
      description: "We respect your deadlines. Every project is managed with Agile sprints and milestone sign-offs to ensure efficient delivery without compromising quality.",
    },
    {
      title: "Long-Term Support & Maintenance",
      description: "Our commitment doesn't end at launch. We provide continuous technical support, updates, and maintenance so your product keeps performing at its best.",
    },
  ];

  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const greenText    = isDark ? "text-green-400" : "text-green-700";
  const cardBg       = isDark ? "bg-gray-900"    : "bg-[#1a2e1a]";
  const dotBorder    = isDark ? "border-black"   : "border-transparent";

  return (
    // ✅ SEO: <section> + aria-labelledby replaces <div>
    // ✅ Spacing: removed -mt-16, added pt-10 so engineering cards above breathe
    <section
      aria-labelledby="why-trust-heading"
      className={`relative overflow-hidden flex items-center justify-center pt-0 pb-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark ? "bg-black" : "bg-transparent"
      }`}
      style={isDark ? darkGridStyle : lightGridStyle}
    >
      {/* Light mode atmospheric glows */}
      {!isDark && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <div style={{
            position: "absolute", top: "0%", left: "-8%",
            width: "45vw", height: "45vw",
            background: "radial-gradient(circle, rgba(74,222,128,0.10) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
          <div style={{
            position: "absolute", top: "0%", right: "-8%",
            width: "40vw", height: "40vw",
            background: "radial-gradient(circle, rgba(251,146,60,0.08) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
        </div>
      )}

      <div className="relative w-full max-w-4xl" style={{ zIndex: 1 }}>

        {/* ✅ SEO: label above heading for context */}
        <p className={`text-sm font-semibold uppercase tracking-widest text-center ${greenText} mb-1`}>
          Why Choose Fiieve
        </p>

        {/* ✅ SEO: H2 (not H1 — this is a sub-section inside the Services page) */}
        <h2
          id="why-trust-heading"
          className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${greenHeading} mb-8 sm:mb-10 text-center`}
        >
          Why Businesses{" "}
          <span className={isDark ? "text-white" : "text-gray-900"}>Trust Us</span>
        </h2>

        {/* Timeline */}
        {/* ✅ SEO: <ol> signals ordered/sequential content to crawlers */}
        <ol className="relative" aria-label="Reasons to choose Fiieve">
          {/* Vertical line */}
          <div className="hidden sm:block absolute left-6 top-0 bottom-16 w-0.5 bg-green-400" aria-hidden="true" />

          <div className="space-y-6 sm:space-y-8">
            {items.map((item, index) => (
              <motion.li
                key={index}
                className="relative flex items-start gap-4 sm:gap-6 list-none"
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.2, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* Circle dot */}
                <div
                  className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-green-400 rounded-full border-4 ${dotBorder} flex items-center justify-center z-10`}
                  aria-hidden="true"
                >
                  <div className="w-3 h-3 bg-black rounded-full" />
                </div>

                {/* Content card */}
                <motion.div
                  className={`flex-1 border-2 border-transparent hover:border-green-400 rounded-lg p-4 sm:p-6 ${cardBg} hover:shadow-lg hover:shadow-green-400/20 transition-all duration-300`}
                  whileHover={{ scale: 1.03 }}
                >
                  {/* ✅ SEO: H3 — correct hierarchy under section H2 */}
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-green-400 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-white text-sm sm:text-base leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              </motion.li>
            ))}
          </div>
        </ol>
      </div>
    </section>
  );
}